/**
 * usePrices Hook
 *
 * Fetches and caches pricing data from backend.
 * Provides loading and error states for UI handling.
 */

import { useState, useEffect, useCallback } from 'react'
import { fetchPrices } from '../config/pricing'

// Simple in-memory cache to avoid re-fetching on every mount
let pricesCache = null
let cacheTimestamp = null
const CACHE_DURATION_MS = 5 * 60 * 1000 // 5 minutes

/**
 * Check if cache is still valid.
 * @returns {boolean}
 */
function isCacheValid() {
  if (!pricesCache || !cacheTimestamp) return false
  return Date.now() - cacheTimestamp < CACHE_DURATION_MS
}

/**
 * Hook to fetch and manage pricing data.
 *
 * @param {Object} options - Hook options
 * @param {boolean} [options.skipCache=false] - Force fresh fetch, bypassing cache
 * @returns {{
 *   prices: import('../config/pricing').PriceData | null,
 *   loading: boolean,
 *   error: string | null,
 *   refetch: () => Promise<void>
 * }}
 *
 * @example
 * const { prices, loading, error } = usePrices()
 *
 * if (loading) return <Skeleton />
 * if (error || !prices) return <CopyOnlyFallback />
 *
 * return <PriceDisplay price={prices.subscription.monthly} />
 */
export function usePrices(options = {}) {
  const { skipCache = false } = options

  const [prices, setPrices] = useState(isCacheValid() ? pricesCache : null)
  const [loading, setLoading] = useState(!isCacheValid())
  const [error, setError] = useState(null)

  const loadPrices = useCallback(async (bypassCache = false) => {
    // Use cache if valid and not bypassing
    if (!bypassCache && isCacheValid()) {
      setPrices(pricesCache)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await fetchPrices()

      if (data) {
        // Update cache
        pricesCache = data
        cacheTimestamp = Date.now()
        setPrices(data)
      } else {
        // fetchPrices returns null on error or 503
        // This is not necessarily an error state - Stripe may just not be configured
        setPrices(null)
      }
    } catch (err) {
      console.error('[usePrices] Unexpected error:', err)
      setError(err.message || 'Failed to load prices')
      setPrices(null)
    } finally {
      setLoading(false)
    }
  }, [])

  // Fetch on mount
  useEffect(() => {
    loadPrices(skipCache)
  }, [loadPrices, skipCache])

  // Manual refetch function (always bypasses cache)
  const refetch = useCallback(() => loadPrices(true), [loadPrices])

  return {
    prices,
    loading,
    error,
    refetch,
  }
}

/**
 * Clear the prices cache.
 * Useful after a purchase or plan change.
 */
export function clearPricesCache() {
  pricesCache = null
  cacheTimestamp = null
}

export default usePrices
